import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { allAdditionalArticles } from '@/mocks/articles-index';

const allArticleLinks = [
  { slug: 'oopbuy-spreadsheet-guide-deutschland', title: 'Ultimativer Guide 2026' },
  ...allAdditionalArticles.map((a) => ({ slug: a.slug, title: a.title })),
];

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [mobileGuidesOpen, setMobileGuidesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideDropdownRef.current && !guideDropdownRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link
            to="/"
            className="text-lg md:text-xl font-heading font-semibold tracking-tight whitespace-nowrap"
            style={{ color: scrolled ? 'oklch(var(--foreground-950))' : '#ffffff' }}
          >
            oopbuyspread<span className="text-accent-500">.</span>boats
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-medium whitespace-nowrap transition-colors hover:text-accent-500"
              style={{ color: scrolled ? 'oklch(var(--foreground-700))' : '#ffffff' }}
            >
              Startseite
            </Link>

            {/* Kategorien dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 text-sm font-medium whitespace-nowrap transition-colors hover:text-accent-500 cursor-pointer"
                style={{ color: scrolled ? 'oklch(var(--foreground-700))' : '#ffffff' }}
              >
                Kategorien
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/80 py-2 animate-fade-in"
                  style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-5 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                    >
                      {cat.nameDe}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Einkaufsratgeber dropdown */}
            <div ref={guideDropdownRef} className="relative">
              <button
                onClick={() => { setGuideDropdownOpen(!guideDropdownOpen); setDropdownOpen(false); }}
                className="flex items-center gap-1 text-sm font-medium whitespace-nowrap transition-colors hover:text-accent-500 cursor-pointer"
                style={{ color: scrolled ? 'oklch(var(--foreground-700))' : '#ffffff' }}
              >
                Einkaufsratgeber
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${guideDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {guideDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-80 bg-background-50 rounded-lg border border-background-200/80 py-2 animate-fade-in max-h-[60vh] overflow-y-auto"
                  style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
                  {allArticleLinks.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/artikel/${article.slug}`}
                      className="block px-5 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                    >
                      {article.title.length > 60 ? article.title.slice(0, 60) + '...' : article.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* External CTA */}
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-medium px-5 py-2 rounded-full bg-accent-500 text-white hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy it now
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label={mobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
            style={{ color: scrolled ? 'oklch(var(--foreground-950))' : '#ffffff' }}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 animate-fade-in max-h-[85vh] overflow-y-auto">
          <div className="px-6 py-4 flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="py-3 text-sm font-medium text-foreground-700 hover:text-accent-500 transition-colors flex items-center gap-2"
            >
              <i className="ri-home-4-line text-base text-foreground-400"></i>
              Startseite
            </Link>

            {/* Categories collapsible */}
            <button
              onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
              className="py-3 text-sm font-semibold text-foreground-500 uppercase tracking-wider flex items-center justify-between cursor-pointer"
            >
              <span>Kategorien ({categories.length})</span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCategoriesOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileCategoriesOpen && (
              <div className="pl-2 border-l-2 border-background-200 ml-2 space-y-0">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 py-2 pl-4 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0">
                      <img src={cat.image} alt="" className="w-full h-full object-cover" />
                    </div>
                    {cat.nameDe}
                  </a>
                ))}
              </div>
            )}

            {/* Guides collapsible */}
            <button
              onClick={() => setMobileGuidesOpen(!mobileGuidesOpen)}
              className="py-3 text-sm font-semibold text-foreground-500 uppercase tracking-wider flex items-center justify-between cursor-pointer"
            >
              <span>Einkaufsratgeber ({allArticleLinks.length})</span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileGuidesOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileGuidesOpen && (
              <div className="pl-2 border-l-2 border-background-200 ml-2 space-y-0">
                {allArticleLinks.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/artikel/${article.slug}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 py-2 pl-4 text-xs text-foreground-600 hover:text-primary-600 transition-colors"
                  >
                    <i className="ri-article-line text-sm text-foreground-400 flex-shrink-0"></i>
                    {article.title.length > 50 ? article.title.slice(0, 50) + '...' : article.title}
                  </Link>
                ))}
              </div>
            )}

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 py-3 text-sm font-medium text-center rounded-full bg-accent-500 text-white hover:bg-accent-600 transition-all"
            >
              Buy it now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}